/**
 * Hover tooltips.
 *
 * Deliberately not the browser's `title` attribute, which was what the slots
 * used before: it waits about a second before appearing, cannot be styled, and
 * looks like it belongs to a web page rather than to the game.
 *
 * One element, moved and refilled, rather than one per slot - there are 45
 * slots plus the recipe book, and only ever one is hovered.
 */

/** Distance from the cursor, so the pointer never covers the first letter. */
const OFFSET_X = 14;
const OFFSET_Y = 18;
/** Keep this far from the window edge before flipping to the other side. */
const EDGE_MARGIN = 8;

export interface TooltipContent {
  title: string;
  /** Optional second line: what the thing is for. */
  hint?: string;
}

export class Tooltip {
  private readonly el: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly hintEl: HTMLDivElement;

  /** Set while a stack rides the cursor; a tooltip would fight with it. */
  suppressed = false;

  private visible = false;

  constructor(root: HTMLElement) {
    this.el = document.createElement("div");
    this.el.id = "tooltip";
    this.el.hidden = true;

    this.titleEl = document.createElement("div");
    this.titleEl.className = "tooltip-title";
    this.hintEl = document.createElement("div");
    this.hintEl.className = "tooltip-hint";

    this.el.append(this.titleEl, this.hintEl);
    root.appendChild(this.el);
  }

  show(content: TooltipContent, x: number, y: number): void {
    if (this.suppressed) return;
    this.titleEl.textContent = content.title;
    this.hintEl.textContent = content.hint ?? "";
    this.hintEl.hidden = content.hint === undefined;
    this.el.hidden = false;
    this.visible = true;
    this.move(x, y);
  }

  move(x: number, y: number): void {
    if (!this.visible) return;

    // Measure after filling, so the flip uses the real width.
    const rect = this.el.getBoundingClientRect();
    let left = x + OFFSET_X;
    let top = y + OFFSET_Y;

    // Flip rather than clamp: a tooltip pinned to the edge covers what you are
    // hovering, and the recipe book lives against the right-hand side.
    if (left + rect.width > window.innerWidth - EDGE_MARGIN) {
      left = x - OFFSET_X - rect.width;
    }
    if (top + rect.height > window.innerHeight - EDGE_MARGIN) {
      top = y - OFFSET_Y - rect.height;
    }

    this.el.style.left = `${Math.max(EDGE_MARGIN, left)}px`;
    this.el.style.top = `${Math.max(EDGE_MARGIN, top)}px`;
  }

  hide(): void {
    this.visible = false;
    this.el.hidden = true;
  }

  /**
   * Wire an element up.
   *
   * `content` is a function rather than a value because a slot's contents
   * change under the cursor - taking a stack out of a hovered slot must empty
   * its tooltip, not leave the old name floating there.
   */
  attach(el: HTMLElement, content: () => TooltipContent | null): void {
    const update = (e: MouseEvent): void => {
      const next = content();
      if (next === null) {
        this.hide();
        return;
      }
      this.show(next, e.clientX, e.clientY);
    };

    el.addEventListener("mouseenter", update);
    el.addEventListener("mousemove", update);
    el.addEventListener("mouseleave", () => this.hide());
    // Clicking changes what the slot holds, so refresh in place.
    el.addEventListener("mousedown", (e) => {
      // After the click has been handled, not before.
      setTimeout(() => update(e), 0);
    });
  }
}
